import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Trophy,
  RefreshCw,
  Users,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Search,
} from 'lucide-react';
import FormStreak from './FormStreak';

/*
 * "Explore the Flight" — a captain-facing scout board over the competitive-
 * intelligence data we already scrape from TennisRecord. It reuses the existing
 * GET /teams/:id/competitive-intelligence payload (no new endpoint): your team
 * plus every opponent on your schedule, with rosters, ratings, W-L records, and
 * full season results.
 *
 * Scope note (intentional, see investigation): the data is opponent-centric, so
 * this covers your team + your direct opponents. Teams in the flight you never
 * play directly aren't profiled, so they don't appear as their own row — only as
 * the "vs" side inside an opponent's results.
 */

// ---- Wire types: mirror app/schemas CompetitiveIntelligenceResponse ---------

export interface TeamPlayer {
  id: number;
  name: string;
  email?: string;
  ntrp_rating: number | null;
  dynamic_rating: number | null;
}

export interface OpponentPlayer {
  id?: number;
  opponent_team_id?: number;
  name: string;
  ntrp_rating: number | null;
  dynamic_rating: number | null;
}

export interface OpponentRecord {
  wins: number;
  losses: number;
  completed_count: number;
  scheduled_count: number;
  recent_form: string[]; // newest first, e.g. ["W", "L", "W"]
}

export interface OpponentMatchSummary {
  match_date: string;
  other_team_name: string;
  other_team_url: string | null;
  is_us: boolean; // true when the "other team" is one of our managed teams
  opponent_won: boolean | null; // from the opponent's POV; null = unplayed
  overall_score: string | null;
  match_result_url: string | null;
}

export interface OpponentTeamStats {
  opponent_team_id: number | null;
  opponent_name: string;
  player_count: number;
  avg_dynamic_rating: number | null;
  median_dynamic_rating: number | null;
  min_dynamic_rating: number | null;
  max_dynamic_rating: number | null;
  players: OpponentPlayer[];
  record: OpponentRecord | null;
  schedule: OpponentMatchSummary[];
  last_match_date: string | null;
}

export interface CompetitiveIntelligence {
  team_id: number;
  team_name: string;
  tennisrecord_url?: string | null;
  last_updated?: string | null;
  team_players: TeamPlayer[];
  opponent_teams: OpponentTeamStats[];
}

// ---- View-model types --------------------------------------------------------

/** One completed/scheduled result, normalized to the row team's point of view. */
export interface FlightResult {
  date: string;
  vsTeamName: string;
  vsTeamUrl: string | null;
  rowTeamWon: boolean | null; // did THIS row's team win? null = unplayed
  score: string | null; // overall line score from the row team's POV
  resultUrl: string | null;
  isUs: boolean; // the "vs" team is your managed team (head-to-head)
}

/** A single team in the flight (your team or an opponent), normalized. */
export interface FlightTeamRow {
  key: string;
  name: string;
  isOurTeam: boolean;
  wins: number;
  losses: number;
  completedCount: number;
  recentForm: string[]; // newest first
  playerCount: number;
  avgRating: number | null;
  minRating: number | null;
  maxRating: number | null;
  players: { name: string; ntrp: number | null; dynamic: number | null }[];
  results: FlightResult[]; // newest first
}

/** A single player across the whole flight, for the leaderboard view. */
export interface FlightPlayerRow {
  key: string;
  name: string;
  teamName: string;
  isOurTeam: boolean;
  dynamic: number | null;
  ntrp: number | null;
  flightRank: number | null; // rank by dynamic rating across the flight (1 = highest)
}

export type TeamSortKey = 'rating' | 'record';
export type PlayerSortKey = 'dynamic' | 'ntrp' | 'name';

// ---- Pure helpers (exported for unit tests) ---------------------------------

/** Coerce a possibly-stringified numeric to a finite number or null. */
export const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

const avg = (xs: number[]): number | null =>
  xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null;

/** "3-2" -> "2-3". Used to re-express an opponent's score from our POV. */
const flipScore = (s: string | null): string | null => {
  if (!s) return s;
  const m = s.match(/^(\d+)\s*-\s*(\d+)$/);
  return m ? `${m[2]}-${m[1]}` : s;
};

const byDateDesc = (a: { date: string }, b: { date: string }): number =>
  new Date(b.date).getTime() - new Date(a.date).getTime();

/** nulls always sort to the bottom, regardless of direction. */
const nullsLast = (
  a: number | null,
  b: number | null,
  dir: 'asc' | 'desc' = 'desc'
): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === 'desc' ? b - a : a - b;
};

/** Case-insensitive team-name equality (TR team names are stable strings). */
const sameTeam = (a: string | null | undefined, b: string | null | undefined): boolean =>
  (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

/**
 * Is this opponent-schedule fixture a match against OUR team? Prefer the
 * backend's is_us link (set when our_team_id was resolved by URL), but fall
 * back to a team-name match: opponents' schedule rows frequently carry no
 * other_team_url, so our_team_id never links and is_us stays false even though
 * the fixture is plainly against us. Without this fallback your own team shows
 * an empty record/streak.
 */
const isAgainstUs = (m: OpponentMatchSummary, ourTeamName: string): boolean =>
  m.is_us || sameTeam(m.other_team_name, ourTeamName);

/** Your team's row, derived from opponents' schedules (fixtures against us). */
export function buildOurTeamRow(intel: CompetitiveIntelligence): FlightTeamRow {
  const ratings = (intel.team_players || [])
    .map((p) => toNum(p.dynamic_rating))
    .filter((n): n is number => n !== null);

  // Each of our fixtures appears exactly once — inside the specific opponent's
  // schedule. Detect it by is_us OR a team-name match (see isAgainstUs);
  // opponent_won is from the opponent's POV, so invert it for our row.
  const ourName = intel.team_name;
  const results: FlightResult[] = [];
  for (const opp of intel.opponent_teams || []) {
    for (const m of opp.schedule || []) {
      if (!isAgainstUs(m, ourName)) continue;
      results.push({
        date: m.match_date,
        vsTeamName: opp.opponent_name,
        vsTeamUrl: null,
        rowTeamWon: m.opponent_won === null ? null : !m.opponent_won,
        score: flipScore(m.overall_score),
        resultUrl: m.match_result_url,
        isUs: false,
      });
    }
  }
  results.sort(byDateDesc);

  const completed = results.filter((r) => r.rowTeamWon !== null);
  const wins = completed.filter((r) => r.rowTeamWon === true).length;
  const losses = completed.length - wins;

  return {
    key: `team-${intel.team_id}`,
    name: intel.team_name,
    isOurTeam: true,
    wins,
    losses,
    completedCount: completed.length,
    recentForm: completed.slice(0, 3).map((r) => (r.rowTeamWon ? 'W' : 'L')),
    playerCount: (intel.team_players || []).length,
    avgRating: avg(ratings),
    minRating: ratings.length ? Math.min(...ratings) : null,
    maxRating: ratings.length ? Math.max(...ratings) : null,
    players: (intel.team_players || []).map((p) => ({
      name: p.name,
      ntrp: toNum(p.ntrp_rating),
      dynamic: toNum(p.dynamic_rating),
    })),
    results,
  };
}

function buildOpponentRow(opp: OpponentTeamStats, ourTeamName: string): FlightTeamRow {
  const rec = opp.record;
  const results: FlightResult[] = (opp.schedule || [])
    .map((m) => ({
      date: m.match_date,
      vsTeamName: m.other_team_name,
      vsTeamUrl: m.other_team_url,
      rowTeamWon: m.opponent_won, // opponent IS the row team here
      score: m.overall_score,
      resultUrl: m.match_result_url,
      isUs: isAgainstUs(m, ourTeamName), // tags the head-to-head fixture vs our team
    }))
    .sort(byDateDesc);

  return {
    key: `opp-${opp.opponent_team_id ?? opp.opponent_name}`,
    name: opp.opponent_name,
    isOurTeam: false,
    wins: rec?.wins ?? 0,
    losses: rec?.losses ?? 0,
    completedCount: rec?.completed_count ?? 0,
    recentForm: rec?.recent_form ?? [],
    playerCount: opp.player_count,
    avgRating: toNum(opp.avg_dynamic_rating),
    minRating: toNum(opp.min_dynamic_rating),
    maxRating: toNum(opp.max_dynamic_rating),
    players: (opp.players || []).map((p) => ({
      name: p.name,
      ntrp: toNum(p.ntrp_rating),
      dynamic: toNum(p.dynamic_rating),
    })),
    results,
  };
}

/** All teams in the flight (your team first), unsorted beyond that. */
export function buildFlightTeams(intel: CompetitiveIntelligence): FlightTeamRow[] {
  return [
    buildOurTeamRow(intel),
    ...(intel.opponent_teams || []).map((opp) => buildOpponentRow(opp, intel.team_name)),
  ];
}

export function sortTeams(rows: FlightTeamRow[], key: TeamSortKey): FlightTeamRow[] {
  const out = [...rows];
  if (key === 'record') {
    out.sort((a, b) => b.wins - a.wins || a.losses - b.losses);
  } else {
    out.sort((a, b) => nullsLast(a.avgRating, b.avgRating, 'desc'));
  }
  return out;
}

/** Flatten every player across the flight and rank them by dynamic rating. */
export function buildFlightPlayers(intel: CompetitiveIntelligence): FlightPlayerRow[] {
  const rows: Omit<FlightPlayerRow, 'flightRank'>[] = [];

  for (const p of intel.team_players || []) {
    rows.push({
      key: `our-${p.id}`,
      name: p.name,
      teamName: intel.team_name,
      isOurTeam: true,
      dynamic: toNum(p.dynamic_rating),
      ntrp: toNum(p.ntrp_rating),
    });
  }
  for (const opp of intel.opponent_teams || []) {
    for (const p of opp.players || []) {
      rows.push({
        key: `opp-${opp.opponent_team_id ?? opp.opponent_name}-${p.id ?? p.name}`,
        name: p.name,
        teamName: opp.opponent_name,
        isOurTeam: false,
        dynamic: toNum(p.dynamic_rating),
        ntrp: toNum(p.ntrp_rating),
      });
    }
  }

  // Flight rank is by dynamic rating, independent of the table's current sort,
  // so "#3" always means the 3rd-highest-rated player in the flight.
  const rankByKey = new Map<string, number>();
  [...rows]
    .filter((r) => r.dynamic !== null)
    .sort((a, b) => (b.dynamic as number) - (a.dynamic as number))
    .forEach((r, i) => rankByKey.set(r.key, i + 1));

  return rows.map((r) => ({ ...r, flightRank: rankByKey.get(r.key) ?? null }));
}

export function sortPlayers(rows: FlightPlayerRow[], key: PlayerSortKey): FlightPlayerRow[] {
  const out = [...rows];
  if (key === 'name') {
    out.sort((a, b) => a.name.localeCompare(b.name));
  } else if (key === 'ntrp') {
    out.sort((a, b) => nullsLast(a.ntrp, b.ntrp, 'desc') || a.name.localeCompare(b.name));
  } else {
    out.sort((a, b) => nullsLast(a.dynamic, b.dynamic, 'desc') || a.name.localeCompare(b.name));
  }
  return out;
}

// ---- Presentational bits -----------------------------------------------------

const fmt = (n: number | null, digits: number): string =>
  n === null ? '—' : n.toFixed(digits);

const formatSyncedAt = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatMatchDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const YouBadge: React.FC = () => (
  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
    Your team
  </span>
);

const Segmented: React.FC<{
  value: 'teams' | 'players';
  onChange: (v: 'teams' | 'players') => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-50 dark:bg-gray-800">
    {(['teams', 'players'] as const).map((v) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        aria-pressed={value === v}
        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors min-h-[32px] ${
          value === v
            ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {v === 'teams' ? 'Teams' : 'Players'}
      </button>
    ))}
  </div>
);

// ---- Drill-down (roster + recent results for one team) ----------------------

const TeamDetail: React.FC<{ row: FlightTeamRow }> = ({ row }) => {
  const roster = [...row.players].sort((a, b) => nullsLast(a.dynamic, b.dynamic, 'desc'));
  // Display results oldest → newest. row.results is stored newest-first (the
  // Streak chips depend on that), so sort a copy ascending here for the list.
  const results = [...row.results].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 py-3 bg-gray-50/70 dark:bg-gray-800/40">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Roster ({roster.length})
        </h4>
        {roster.length === 0 ? (
          <p className="text-sm text-gray-400">No roster scraped yet.</p>
        ) : (
          <ul className="space-y-1">
            {roster.map((p, idx) => (
              <li key={`${p.name}-${idx}`} className="flex items-center justify-between text-sm">
                <span className="text-gray-800 dark:text-gray-200 truncate">{p.name}</span>
                <span className="ml-2 flex-shrink-0 tabular-nums text-gray-600 dark:text-gray-300">
                  <span className="font-medium">{fmt(p.dynamic, 2)}</span>
                  <span className="text-gray-400 dark:text-gray-500"> · {fmt(p.ntrp, 1)} NTRP</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Results
        </h4>
        {results.length === 0 ? (
          <p className="text-sm text-gray-400">No matches scraped yet.</p>
        ) : (
          <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
            {results.map((r, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm" data-testid="flight-result-row">
                <span className="w-20 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatMatchDate(r.date)}
                </span>
                {r.rowTeamWon === null ? (
                  <span className="inline-block w-3.5 h-3.5 rounded-sm bg-gray-200 dark:bg-gray-600" title="Not played" />
                ) : (
                  <span
                    className={`inline-block w-3.5 h-3.5 leading-[14px] text-center text-[9px] font-bold rounded-sm ${
                      r.rowTeamWon
                        ? 'bg-green-200 text-green-900 dark:bg-green-700 dark:text-green-50'
                        : 'bg-red-200 text-red-900 dark:bg-red-700 dark:text-red-50'
                    }`}
                    title={r.rowTeamWon ? 'Win' : 'Loss'}
                  >
                    {r.rowTeamWon ? 'W' : 'L'}
                  </span>
                )}
                <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-300">
                  vs {r.vsTeamName}
                  {r.isUs && (
                    <span className="ml-1 text-primary-600 dark:text-primary-400 font-medium">(you)</span>
                  )}
                </span>
                {r.score && (
                  <span className="flex-shrink-0 tabular-nums text-gray-500 dark:text-gray-400">{r.score}</span>
                )}
                {r.resultUrl && (
                  <a
                    href={r.resultUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-shrink-0 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                    title="View result on TennisRecord"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// Stable outer chrome for every state (loading/error/empty/data). Defined at
// module scope — NOT inside FlightExplorer — so the search input it wraps keeps
// focus across re-renders instead of remounting on every keystroke.
const FlightShell: React.FC<{
  lastUpdated: string | null | undefined;
  children: React.ReactNode;
}> = ({ lastUpdated, children }) => (
  <div
    className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-5"
    data-testid="flight-explorer"
  >
    <div className="flex items-center gap-3 mb-1">
      <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
        <Trophy className="w-5 h-5 text-primary-600 dark:text-primary-400" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Explore the Flight
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Your team and every opponent on your schedule · synced{' '}
          {formatSyncedAt(lastUpdated)}
        </p>
      </div>
    </div>
    {children}
  </div>
);

// ---- Main component ----------------------------------------------------------

interface Props {
  teamId: number;
}

const FlightExplorer: React.FC<Props> = ({ teamId }) => {
  const [intel, setIntel] = useState<CompetitiveIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<'teams' | 'players'>('teams');
  const [teamSort, setTeamSort] = useState<TeamSortKey>('rating');
  const [playerSort, setPlayerSort] = useState<PlayerSortKey>('dynamic');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`/teams/${teamId}/competitive-intelligence`)
      .then((res) => {
        if (cancelled) return;
        setIntel((res?.data as CompetitiveIntelligence) ?? null);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 403
            ? 'Flight intel is captain-only.'
            : 'Unable to load flight data. Please try again.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const teams = useMemo(() => (intel ? buildFlightTeams(intel) : []), [intel]);
  const players = useMemo(() => (intel ? buildFlightPlayers(intel) : []), [intel]);

  const visibleTeams = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? teams.filter((t) => t.name.toLowerCase().includes(q)) : teams;
    return sortTeams(filtered, teamSort);
  }, [teams, query, teamSort]);

  const visiblePlayers = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? players.filter(
          (p) => p.name.toLowerCase().includes(q) || p.teamName.toLowerCase().includes(q)
        )
      : players;
    return sortPlayers(filtered, playerSort);
  }, [players, query, playerSort]);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (loading) {
    return (
      <FlightShell lastUpdated={intel?.last_updated}>
        <div className="flex items-center justify-center py-10" data-testid="flight-loading">
          <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-500">Loading flight…</span>
        </div>
      </FlightShell>
    );
  }

  if (error) {
    return (
      <FlightShell lastUpdated={intel?.last_updated}>
        <p className="py-10 text-center text-sm text-red-600 dark:text-red-400" data-testid="flight-error">
          {error}
        </p>
      </FlightShell>
    );
  }

  if (!intel || (intel.opponent_teams || []).length === 0) {
    return (
      <FlightShell lastUpdated={intel?.last_updated}>
        <div className="py-8 text-center" data-testid="flight-empty">
          <Users className="w-9 h-9 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            No flight data yet
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto mt-1">
            Sync from TennisRecord on the Lineups tab to scout the other teams and players in your
            flight.
          </p>
        </div>
      </FlightShell>
    );
  }

  return (
    <FlightShell lastUpdated={intel?.last_updated}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-3 mb-3">
        <Segmented value={view} onChange={setView} />
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={view === 'teams' ? 'Search teams…' : 'Search players or teams…'}
              aria-label="Search the flight"
              className="pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/40 w-44 sm:w-56"
            />
          </div>
        </div>
      </div>

      {view === 'teams' ? (
        <div data-testid="flight-view-teams" className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-800/50">
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2">Team</th>
                <th className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setTeamSort('record')}
                    className={`hover:text-gray-700 dark:hover:text-gray-200 ${teamSort === 'record' ? 'text-primary-600 dark:text-primary-400' : ''}`}
                  >
                    Record
                  </button>
                </th>
                <th className="px-3 py-2">Streak</th>
                <th className="px-3 py-2 text-right">Players</th>
                <th className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setTeamSort('rating')}
                    className={`hover:text-gray-700 dark:hover:text-gray-200 ${teamSort === 'rating' ? 'text-primary-600 dark:text-primary-400' : ''}`}
                  >
                    Avg rating
                  </button>
                </th>
                <th className="px-3 py-2 text-right">Range</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {visibleTeams.map((t, idx) => {
                const isOpen = expanded.has(t.key);
                return (
                  <React.Fragment key={t.key}>
                    <tr
                      className={`group cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/40 ${
                        t.isOurTeam ? 'bg-primary-50/60 dark:bg-primary-900/10' : idx % 2 === 1 ? 'bg-gray-50/70 dark:bg-gray-700/20' : ''
                      }`}
                      onClick={() => toggle(t.key)}
                      data-testid="flight-team-row"
                    >
                      <td className="px-3 py-2">
                        <span className="flex items-center">
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 mr-1.5 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 mr-1.5 flex-shrink-0" />
                          )}
                          <span className="font-medium text-gray-900 dark:text-gray-100">{t.name}</span>
                          {t.isOurTeam && <YouBadge />}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">
                        {t.completedCount === 0 ? <span className="text-gray-400">—</span> : `${t.wins}-${t.losses}`}
                      </td>
                      <td className="px-3 py-2">
                        <FormStreak form={t.recentForm} order="chrono" size="sm" />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{t.playerCount}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">{fmt(t.avgRating, 2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                        {t.minRating === null || t.maxRating === null ? '—' : `${fmt(t.minRating, 2)}–${fmt(t.maxRating, 2)}`}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} className="p-0">
                          <TeamDetail row={t} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {visibleTeams.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                    No teams match “{query}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div data-testid="flight-view-players" className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-800/50">
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2 w-12 text-right">#</th>
                <th className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setPlayerSort('name')}
                    className={`hover:text-gray-700 dark:hover:text-gray-200 ${playerSort === 'name' ? 'text-primary-600 dark:text-primary-400' : ''}`}
                  >
                    Player
                  </button>
                </th>
                <th className="px-3 py-2">Team</th>
                <th className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setPlayerSort('dynamic')}
                    className={`hover:text-gray-700 dark:hover:text-gray-200 ${playerSort === 'dynamic' ? 'text-primary-600 dark:text-primary-400' : ''}`}
                  >
                    Dynamic
                  </button>
                </th>
                <th className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => setPlayerSort('ntrp')}
                    className={`hover:text-gray-700 dark:hover:text-gray-200 ${playerSort === 'ntrp' ? 'text-primary-600 dark:text-primary-400' : ''}`}
                  >
                    NTRP
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {visiblePlayers.map((p, idx) => (
                <tr
                  key={p.key}
                  className={`hover:bg-gray-100 dark:hover:bg-gray-700/40 ${
                    p.isOurTeam ? 'bg-primary-50/60 dark:bg-primary-900/10' : idx % 2 === 1 ? 'bg-gray-50/70 dark:bg-gray-700/20' : ''
                  }`}
                  data-testid="flight-player-row"
                >
                  <td className="px-3 py-2 text-right tabular-nums text-gray-400">
                    {p.flightRank ?? '—'}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{p.name}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                    <span className="truncate">{p.teamName}</span>
                    {p.isOurTeam && <YouBadge />}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">{fmt(p.dynamic, 2)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">{fmt(p.ntrp, 1)}</td>
                </tr>
              ))}
              {visiblePlayers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                    No players match “{query}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </FlightShell>
  );
};

export default FlightExplorer;
